import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { auth } from '../../lib/supabase'
import { supabaseAuthAPI } from '../../services/supabaseAuth'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card'
import { Alert, AlertDescription } from '../../components/ui/alert'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import ImageWithFallback from '../../components/ui/ImageWithFallback'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [resetSuccess, setResetSuccess] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm()

  useEffect(() => {
    const initialize = async () => {
      setErrorMessage('')
      setStatusMessage('')

      const recoveryType = searchParams.get('type') === 'recovery'

      try {
        let sessionResponse = await auth.getCurrentSession()

        if (!sessionResponse.data?.session && recoveryType && typeof auth.getSessionFromUrl === 'function') {
          const { data, error } = await auth.getSessionFromUrl()
          if (error) {
            throw error
          }
          sessionResponse = await auth.getCurrentSession()
        }

        if (sessionResponse.data?.session) {
          setIsReady(true)
        } else {
          setStatusMessage(
            'Your password reset link is not active. Please request a new reset email and follow the link from your inbox.'
          )
        }
      } catch (error) {
        setErrorMessage(error.message || 'Unable to process the reset link.')
      }
    }

    initialize()
  }, [searchParams])

  const onSubmit = async (data) => {
    setIsLoading(true)
    setErrorMessage('')
    setStatusMessage('')

    try {
      const result = await supabaseAuthAPI.resetPasswordConfirm(data.password)
      if (result.data) {
        setStatusMessage(result.data.message)
        setResetSuccess(true)
      }
    } catch (error) {
      setErrorMessage(error.message || 'Unable to reset your password.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center aurora-bg py-12 px-4 sm:px-6 lg:px-8 relative">
      <div className="absolute inset-0 golden-mesh opacity-30"></div>
      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <ImageWithFallback
                src="/images/zova_labs_right.png"
                alt="ZovaLabs Logo"
                className="w-16 h-16 object-contain animate-gold-pulse"
              />
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-sparkle"></div>
            </div>
          </div>
          <h1 className="text-4xl font-display font-bold text-foreground mb-2">Reset Your Password</h1>
          <p className="text-muted-foreground">
            Enter a new password to complete your password recovery.
          </p>
        </div>

        <Card variant="luxury" className="shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-display text-center">New Password</CardTitle>
            <CardDescription className="text-center">
              Set a secure password and then sign in again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {errorMessage && (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {statusMessage && (
              <Alert>
                <AlertDescription>{statusMessage}</AlertDescription>
              </Alert>
            )}

            {resetSuccess ? (
              <div className="space-y-6 text-center">
                <div className="space-y-2">
                  <h2 className="text-xl font-semibold text-foreground">Password updated successfully</h2>
                  <p className="text-sm text-muted-foreground">
                    Your password has been reset. You can now sign in with your new password.
                  </p>
                </div>
                <Button variant="gold" size="lg" className="w-full shadow-2xl" asChild>
                  <Link to="/login">Go to Sign In</Link>
                </Button>
              </div>
            ) : isReady ? (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">
                    New Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your new password"
                    className={errors.password ? 'border-destructive' : ''}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters',
                      },
                    })}
                  />
                  {errors.password && (
                    <p className="text-sm text-destructive">{errors.password.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Confirm your new password"
                    className={errors.confirmPassword ? 'border-destructive' : ''}
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) => value === watch('password') || 'Passwords do not match',
                    })}
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
                  )}
                </div>

                <Button type="submit" variant="gold" size="lg" className="w-full shadow-2xl" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <LoadingSpinner size="sm" color="text-primary-foreground" />
                      <span className="ml-2">Updating password...</span>
                    </>
                  ) : (
                    'Update password'
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-4 p-4 text-center text-sm text-muted-foreground">
                <p>{statusMessage || 'Checking your recovery link...'}</p>
                <p>
                  If you do not have a valid recovery link, request a new reset email from the{' '}
                  <Link className="text-primary hover:text-primary/80" to="/forgot-password">
                    forgot password page
                  </Link>.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
